import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import {
  TrendingUp, TrendingDown, Minus, Activity
} from 'lucide-react';

export const KPITrendsTab = ({ trends, dateRange, setDateRange, loadData, analytics }) => (
  <div className="space-y-6">
    <Card>
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <CardTitle className="text-lg">Performance Trend</CardTitle>
            <CardDescription>
              {dateRange.from || dateRange.to
                ? `Custom range${dateRange.from ? ` from ${dateRange.from}` : ''}${dateRange.to ? ` to ${dateRange.to}` : ''}`
                : 'Daily average performance — last 30 days'}
            </CardDescription>
          </div>
          <div className="flex items-center gap-2 flex-wrap" data-testid="date-range-filter">
            <Input type="date" value={dateRange.from}
              onChange={(e) => setDateRange(prev => ({ ...prev, from: e.target.value }))}
              className="w-[150px] h-9 text-sm" data-testid="date-from" />
            <span className="text-muted-foreground text-sm">to</span>
            <Input type="date" value={dateRange.to}
              onChange={(e) => setDateRange(prev => ({ ...prev, to: e.target.value }))}
              className="w-[150px] h-9 text-sm" data-testid="date-to" />
            <Button variant="outline" size="sm" onClick={() => { loadData(); }} data-testid="apply-date-filter">Apply</Button>
            {(dateRange.from || dateRange.to) && (
              <Button variant="ghost" size="sm" onClick={() => { setDateRange({ from: '', to: '' }); setTimeout(loadData, 0); }} data-testid="clear-date-filter">Clear</Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {trends.length > 0 ? (
          <div className="h-[400px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={trends}>
                <defs>
                  <linearGradient id="performanceGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(value) => value.slice(5)} />
                <YAxis tick={{ fontSize: 12 }} domain={[0, 'auto']} tickFormatter={(value) => `${value}%`} />
                <Tooltip 
                  formatter={(value, name) => {
                    if (name === 'performance') return [`${value}%`, 'Performance'];
                    return [value, name];
                  }}
                  contentStyle={{ borderRadius: '4px', border: '1px solid #e4e4e7' }}
                />
                <Area type="monotone" dataKey="performance" stroke="#3b82f6" strokeWidth={2} fill="url(#performanceGradient)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="h-[400px] flex items-center justify-center text-muted-foreground">
            <div className="text-center">
              <Activity className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No trend data available yet</p>
              <p className="text-sm">Add KPIs and update their values over time to see trends</p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>

    <div className="grid md:grid-cols-3 gap-4">
      <Card className="bg-emerald-50 border-emerald-200">
        <CardContent className="p-4 text-center">
          <TrendingUp className="h-8 w-8 text-emerald-600 mx-auto mb-2" />
          <p className="text-3xl font-bold text-emerald-600">{analytics?.by_trend?.up || 0}</p>
          <p className="text-sm text-emerald-700">Improving</p>
        </CardContent>
      </Card>
      <Card className="bg-gray-50 border-gray-200">
        <CardContent className="p-4 text-center">
          <Minus className="h-8 w-8 text-gray-600 mx-auto mb-2" />
          <p className="text-3xl font-bold text-gray-600">{analytics?.by_trend?.stable || 0}</p>
          <p className="text-sm text-gray-700">Stable</p>
        </CardContent>
      </Card>
      <Card className="bg-rose-50 border-rose-200">
        <CardContent className="p-4 text-center">
          <TrendingDown className="h-8 w-8 text-rose-600 mx-auto mb-2" />
          <p className="text-3xl font-bold text-rose-600">{analytics?.by_trend?.down || 0}</p>
          <p className="text-sm text-rose-700">Declining</p>
        </CardContent>
      </Card>
    </div>
  </div>
);
